'use client';

import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  addDays,
  subDays,
  isBefore,
  lastDayOfMonth,
  format,
  startOfDay,
  startOfWeek,
  parseISO,
  differenceInDays,
} from 'date-fns';
import {
  Button as MokoButton,
  Tooltip,
  FilterSelect,
  type FilterSelectOption,
  DatePicker as MokoDatePicker,
  Link as MokoLink,
  Separator,
  Skeleton,
  SkeletonReveal,
} from '@mokoboko/components';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import {
  ClassCard,
  ClassCardSkeleton,
  ClassesLoadError,
  EmptyCalendarState,
  NoClassesYet,
} from '@/components';
import useAvailableClasses, { AvailableDay } from '@/state/available-classes';
import { AppearanceSettings } from '@/state/appearance-settings';
import {
  getUserTimezone,
  getAbbreviatedName,
  mergeClassNames,
  parseDateParam,
} from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-is-mobile';
import {
  buildDayMapByKey,
  doesClassMatchFilters,
  findClosestClassMatch,
  getClassListOptions,
  getInstructorListOptions,
  getInstructorsFromAvailability,
  getMobileWeekStart,
  getWeekStart,
  getWindowsToDate,
  mapClassData,
  parseSelectedCsv,
} from '@/helpers/class';

import { type Class } from '@/state/class-groups';
import { useClassAvailability } from '@/hooks/use-offering-availability';
import { useDateFilter } from '@/hooks/use-date-filter';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';

const getInstructorAbbreviated = (
  list: { admin_user_id: string; first_name: string; last_name: string }[],
  id: string
): string => {
  const found = list.find((i) => i.admin_user_id === id);
  return found ? getAbbreviatedName(found.first_name, found.last_name) : '';
};

const ClosestAvailableClass = ({
  selectedClass,
  selectedInstructor,
  selectedDate,
  allClasses,
  nextWeekClasses,
  setSelectedDate,
  classList,
  instructorList,
  handleWeekChange,
  isMobile,
  visibleDays,
  setIsClosestClassNavigation,
  currentWeekStart,
}: {
  selectedClass: string;
  selectedInstructor: string;
  selectedDate: Date;
  allClasses: AvailableDay[];
  nextWeekClasses: AvailableDay[];
  setSelectedDate: (date: Date) => void;
  classList: Class[];
  instructorList: {
    admin_user_id: string;
    first_name: string;
    last_name: string;
    profile_picture: string | null;
  }[];
  handleWeekChange: (windows: number) => void;
  isMobile: boolean;
  visibleDays: number;
  setIsClosestClassNavigation: (value: boolean) => void;
  currentWeekStart: Date;
}) => {
  const selectedDayStart = startOfDay(selectedDate);
  const todayStart = startOfDay(new Date());

  const selectedClassesArray = parseSelectedCsv(selectedClass);
  const selectedInstructorsArray = parseSelectedCsv(selectedInstructor);

  const findClosest = (days: AvailableDay[]) =>
    findClosestClassMatch(
      days,
      selectedClassesArray,
      selectedInstructorsArray,
      todayStart,
      selectedDayStart
    );
  // Next week is only a fallback: a match in the visible week keeps the user
  // on that week even when next week has a closer one.
  const closestMatch = findClosest(allClasses) ?? findClosest(nextWeekClasses);

  const closestDate = closestMatch?.date ?? null;
  const closestName = closestMatch?.name ?? null;
  const closestInstructor = closestMatch?.instructor ?? null;

  const formattedDate = closestDate
    ? format(
        closestDate,
        closestDate.getFullYear() === new Date().getFullYear()
          ? 'MMM d'
          : 'MMM d, yyyy'
      )
    : null;
  const getInstructorShortName = (
    ins: { first_name: string; last_name: string } | null
  ) => (ins ? getAbbreviatedName(ins.first_name, ins.last_name) : null);
  const fullInstructorName = getInstructorShortName(closestInstructor);

  const showClassEmptyState = (
    text: string,
    subText: React.ReactNode,
    buttonDate?: Date,
    subTextClass?: string
  ) => {
    return (
      <EmptyCalendarState
        className="py-6"
        title={text}
        description={<span className={subTextClass}>{subText}</span>}
        primaryAction={
          buttonDate ? (
            <MokoButton
              size="md"
              variant="secondary-gray"
              onClick={() => {
                setIsClosestClassNavigation(true);
                const windows = getWindowsToDate(
                  buttonDate,
                  currentWeekStart,
                  visibleDays
                );
                if (windows !== 0) handleWeekChange(windows);
                setSelectedDate(new Date(buttonDate));
                track({
                  event: AnalyticsEvent.HOME_EMPTY_STATE_CTA_CLICKED,
                  properties: {
                    target_date: format(buttonDate, 'yyyy-MM-dd'),
                    days_from_selected: Math.abs(
                      differenceInDays(buttonDate, selectedDate)
                    ),
                  },
                });
              }}
            >
              {`Show ${format(new Date(buttonDate), new Date(buttonDate).getFullYear() === new Date().getFullYear() ? 'MMM d' : 'MMM d, yyyy')}`}
            </MokoButton>
          ) : undefined
        }
      />
    );
  };

  if (selectedClass === 'all' && selectedInstructor === 'all') {
    if (!closestDate) {
      return showClassEmptyState(
        'No classes today',
        <>There are no available classes today or in the near future.</>,
        undefined,
        'sm:w-[230px]'
      );
    }

    return showClassEmptyState(
      'No classes today',
      <>
        Closest classes are on{' '}
        <span className="font-semibold text-gray-900">{formattedDate}</span>
      </>,
      closestDate ?? undefined,
      'sm:w-[230px]'
    );
  }

  const selectedClassName =
    selectedClassesArray.length > 0
      ? selectedClassesArray
          .map((id) => classList.find((item) => item.class_id === id)?.name)
          .filter(Boolean)
          .join(', ')
      : '';
  const selectedInstructorName =
    selectedInstructor !== 'all'
      ? getInstructorAbbreviated(instructorList, selectedInstructor)
      : '';

  if (selectedClass !== 'all' && selectedInstructor === 'all') {
    if (!closestDate) {
      return showClassEmptyState(
        'No classes today',
        <>There are no available classes today or in the near future.</>,
        undefined,
        'sm:w-[230px]'
      );
    }

    return showClassEmptyState(
      'No classes today',
      <>
        The next{' '}
        <span className="font-semibold text-gray-900">{closestName}</span>{' '}
        session is on{' '}
        <span className="font-semibold text-gray-900">
          {format(closestDate, 'MMMM d, yyyy')}
        </span>
      </>,
      closestDate
    );
  }

  if (selectedClass === 'all' && selectedInstructor !== 'all') {
    if (!closestDate) {
      return showClassEmptyState(
        `No upcoming classes with ${selectedInstructorName}`,
        <>
          It looks like{' '}
          <span className="font-semibold text-gray-900">
            {selectedInstructorName}
          </span>{' '}
          isn’t offering any classes at the moment. You can pick a different
          instructor or try again later.
        </>,
        undefined,
        'sm:w-[416px]'
      );
    }

    return showClassEmptyState(
      `No classes with ${fullInstructorName} today`,
      <>
        The next available session with{' '}
        <span className="font-semibold text-gray-900">
          {fullInstructorName}
        </span>{' '}
        is on{' '}
        <span className="font-semibold text-gray-900">
          {format(closestDate, 'MMMM d, yyyy')}
        </span>
      </>,
      closestDate
    );
  }

  if (selectedClass !== 'all' && selectedInstructor !== 'all') {
    if (!closestDate) {
      return showClassEmptyState(
        `${selectedInstructorName} doesn’t teach this class`,
        <>
          You’ve selected{' '}
          <span className="font-semibold text-gray-900">
            {selectedClassName}
          </span>
          , but{' '}
          <span className="font-semibold text-gray-900">
            {selectedInstructorName}
          </span>{' '}
          doesn’t offer this class. Please choose another instructor or change
          the class type.
        </>,
        undefined,
        'sm:w-[448px]'
      );
    }

    return showClassEmptyState(
      `No classes with ${fullInstructorName} today`,
      <>
        The next available session with{' '}
        <span className="font-semibold text-gray-900">
          {fullInstructorName}
        </span>{' '}
        is on{' '}
        <span className="font-semibold">
          {format(closestDate, 'MMMM d, yyyy')}
        </span>
      </>,
      closestDate
    );
  }
};

const getVisibleDays = (width: number) => {
  if (width < 640) return 4;
  if (width < 768) return 6;
  return 7;
};

/* Widths of each FilterTrigger in its placeholder state ("<innerLabel>: All"),
   measured rather than derived: SkeletonReveal pins its container to
   max(skeleton, content) for good, so an overwide bar would leave the settled
   control padded. */
const FILTER_WIDTH_MOBILE = 126;
const FILTER_WIDTH_CLASSES = 146;
const FILTER_WIDTH_INSTRUCTORS = 168;

const FILTER_PANEL_MIN_WIDTH = 'max(100%, 14rem)';

const ClassListSkeleton = ({
  classSettings,
}: {
  classSettings: AppearanceSettings['class'];
}) => (
  <>
    <ClassCardSkeleton classSettings={classSettings} />
    <ClassCardSkeleton classSettings={classSettings} />
    <ClassCardSkeleton classSettings={classSettings} />
  </>
);

const FilterTriggerSkeleton = ({ width }: { width: number }) => (
  <Skeleton className="rounded-xl" height={44} width={width} />
);

const NavButtonSkeleton = () => (
  <Skeleton
    isRounded
    width={44}
    height={44}
    className="!rounded-xl sm:!h-10 sm:!w-10"
  />
);

/* The whole of DailyView's loading layout (month row, week strip, class
   cards), for the frames before it can render its own: a parent still
   fetching which tabs exist, and DailyView's first render. Same boxes at the
   same sizes, so the hand-off moves nothing. useIsMobile and getVisibleDays
   switch at 640 and 768, which are sm and md here. */
export const DailyViewSkeleton = ({
  classSettings,
}: {
  classSettings: AppearanceSettings['class'];
}) => (
  <>
    <div className="flex flex-col gap-5">
      <div className="flex flex-row items-center justify-between gap-4">
        <Skeleton text="body" width={128} />
        <div className="sm:hidden">
          <FilterTriggerSkeleton width={FILTER_WIDTH_MOBILE} />
        </div>
        <div className="hidden flex-row flex-wrap gap-3 sm:flex">
          <FilterTriggerSkeleton width={FILTER_WIDTH_CLASSES} />
          <FilterTriggerSkeleton width={FILTER_WIDTH_INSTRUCTORS} />
        </div>
      </div>
      <Separator className="hidden sm:block" />
      <div className="flex items-center justify-between">
        <NavButtonSkeleton />
        <div className="mx-2 flex flex-1 items-center justify-center gap-2 sm:mx-0 sm:gap-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className={mergeClassNames(
                'w-12 flex-col items-center py-1.5 sm:w-16',
                i < 4 ? 'flex' : i < 6 ? 'hidden sm:flex' : 'hidden md:flex'
              )}
            >
              <Skeleton text="body-sm" width={28} />
              <Skeleton text="body-sm" width={20} />
            </div>
          ))}
        </div>
        <NavButtonSkeleton />
      </div>
    </div>
    <ClassListSkeleton classSettings={classSettings} />
  </>
);

const NavButton = ({
  direction,
  isMobile,
  isDisabled,
  isTooltipOpen,
  tooltipText,
  onClick,
  onMobileToggle,
}: {
  direction: 'prev' | 'next';
  isMobile: boolean;
  isDisabled: boolean;
  isTooltipOpen: boolean;
  tooltipText: string;
  onClick: () => void;
  onMobileToggle: () => void;
}) => {
  const label = isDisabled
    ? tooltipText
    : direction === 'prev'
      ? 'Previous week'
      : 'Next week';
  const button = (
    <MokoButton
      aria-label={label}
      className={isMobile && isDisabled ? 'pointer-events-none' : undefined}
      disabled={isDisabled}
      iconOnly
      leadingIcon={direction === 'prev' ? <ChevronLeft /> : undefined}
      trailingIcon={direction === 'next' ? <ChevronRight /> : undefined}
      onClick={onClick}
      size={isMobile ? 'lg' : 'md'}
      variant="secondary-gray"
    />
  );

  if (!isMobile) {
    return (
      <Tooltip content={label} side="top">
        {button}
      </Tooltip>
    );
  }

  return (
    <div className="relative" onClick={isDisabled ? onMobileToggle : undefined}>
      {button}
      {isTooltipOpen && isDisabled && (
        <div
          className={mergeClassNames(
            'absolute -top-11 z-50',
            direction === 'prev' ? 'left-0' : 'right-0'
          )}
        >
          <div className="whitespace-nowrap rounded-xl bg-ink px-2.5 py-2 text-center text-sm font-medium text-white shadow-lg">
            {tooltipText}
            <div
              className={mergeClassNames(
                'absolute -bottom-1.5 border-x-[6px] border-t-[6px] border-x-transparent border-t-ink',
                direction === 'prev' ? 'left-5' : 'right-5'
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const DailyView = ({
  classSettings,
  groupId,
}: {
  classSettings: AppearanceSettings['class'];
  groupId: string | null;
}) => {
  const today = startOfDay(new Date());
  const isMobile = useIsMobile();
  const [visibleDays, setVisibleDays] = useState(
    typeof window !== 'undefined' ? getVisibleDays(window.innerWidth) : 7
  );
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [hoveredNavButton, setHoveredNavButton] = useState<
    'prev' | 'next' | null
  >(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarToggleRef = useRef<HTMLAnchorElement>(null);

  const {
    state: {
      selectedDate,
      currentWeekStart,
      selectedClass,
      selectedInstructor,
      isClosestClassNavigation,
      isRestoredFromStorage,
    },
    isInitialized,
    setSelectedDate,
    setCurrentWeekStart,
    setSelectedClass,
    setSelectedInstructor,
    setIsClosestClassNavigation,
    updateState,
    markAsNotRestored,
  } = useDateFilter();

  const setSelectedClassesArray = (classes: string[]) => {
    if (classes.length === 0) {
      setSelectedClass('all');
    } else {
      setSelectedClass(classes.join(','));
    }
    track({
      event: AnalyticsEvent.HOME_FILTER_CHANGED,
      properties: {
        filter_type: 'class',
        selected_count: classes.length,
        total_count: classList.length,
      },
    });
  };

  const setSelectedInstructorsArray = (instructors: string[]) => {
    if (instructors.length === 0) {
      setSelectedInstructor('all');
    } else {
      setSelectedInstructor(instructors.join(','));
    }
    track({
      event: AnalyticsEvent.HOME_FILTER_CHANGED,
      properties: {
        filter_type: 'instructor',
        selected_count: instructors.length,
        total_count: availableInstructors.length,
      },
    });
  };

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const didInitFromUrl = useRef(false);

  const {
    classes: classList,
    classGroups,
    hasClasses,
    isLoading: isClassGroupsLoading,
  } = useClassAvailability();

  const effectiveStart =
    currentWeekStart && currentWeekStart >= new Date()
      ? currentWeekStart
      : new Date();

  const {
    data: availableClasses,
    isLoading,
    error: availabilityError,
  } = useAvailableClasses({
    start_date: format(effectiveStart, 'yyyy-MM-dd'),
    end_date: format(addDays(effectiveStart, isMobile ? 3 : 6), 'yyyy-MM-dd'),
    timezone: getUserTimezone(),
  });

  // 'full' mode when filters are applied so DatePicker can filter client-side
  const hasFilters =
    (selectedClass && selectedClass !== 'all') ||
    (selectedInstructor && selectedInstructor !== 'all');
  const { data: calendarMonthClasses } = useAvailableClasses({
    // Mirror the +7 end buffer so the first calendar-month week always has prev-window data, even when the 1st lands on Monday.
    start_date: format(
      subDays(
        startOfWeek(
          new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1),
          { weekStartsOn: 1 }
        ),
        7
      ),
      'yyyy-MM-dd'
    ),
    end_date: format(addDays(lastDayOfMonth(calendarMonth), 7), 'yyyy-MM-dd'),
    timezone: getUserTimezone(),
    response_mode: hasFilters ? 'full' : 'summary',
  });

  const calendarActiveDates = useMemo(() => {
    const days = [
      ...(calendarMonthClasses?.days ?? []),
      ...(calendarMonthClasses?.next_week_days ?? []),
    ];
    return days.filter((d) => d.has_classes).map((d) => parseISO(d.date));
  }, [calendarMonthClasses]);

  const calendarActiveDateKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const d of calendarActiveDates) {
      keys.add(format(d, 'yyyy-MM-dd'));
    }
    return keys;
  }, [calendarActiveDates]);

  const calendarDayByKey = useMemo(() => {
    if (!calendarMonthClasses) return new Map<string, AvailableDay>();
    return buildDayMapByKey([
      ...calendarMonthClasses.days,
      ...(calendarMonthClasses.next_week_days ?? []),
    ]);
  }, [calendarMonthClasses]);

  const availableDayByKey = useMemo(
    () => (availableClasses ? buildDayMapByKey(availableClasses.days) : null),
    [availableClasses]
  );

  const selectedClassesArray = useMemo(
    () => parseSelectedCsv(selectedClass),
    [selectedClass]
  );
  const selectedInstructorsArray = useMemo(
    () => parseSelectedCsv(selectedInstructor),
    [selectedInstructor]
  );

  const isPrevWindowEmpty = useMemo(() => {
    if (!currentWeekStart || !calendarMonthClasses) return false;
    const todayLocal = startOfDay(new Date());
    for (let i = 1; i <= visibleDays; i++) {
      const date = subDays(currentWeekStart, i);
      if (isBefore(date, todayLocal)) continue;
      const day = calendarDayByKey.get(format(date, 'yyyy-MM-dd'));
      if (!day) continue;
      if (hasFilters) {
        const hasMatch = day.classes?.some((cls) =>
          doesClassMatchFilters(
            cls,
            selectedClassesArray,
            selectedInstructorsArray
          )
        );
        if (hasMatch) return false;
      } else if (day.has_classes) {
        return false;
      }
    }
    return true;
  }, [
    currentWeekStart,
    visibleDays,
    calendarMonthClasses,
    calendarDayByKey,
    selectedClassesArray,
    selectedInstructorsArray,
    hasFilters,
  ]);

  const isNextWindowEmpty = useMemo(() => {
    if (!currentWeekStart || !calendarMonthClasses) return false;
    // Fail open past fetched data — unknown days might have classes.
    const fetchEnd = addDays(lastDayOfMonth(calendarMonth), 7);
    for (let i = visibleDays; i < visibleDays * 2; i++) {
      const date = addDays(currentWeekStart, i);
      if (date > fetchEnd) return false;
      const day = calendarDayByKey.get(format(date, 'yyyy-MM-dd'));
      if (!day) continue;
      if (hasFilters) {
        const hasMatch = day.classes?.some((cls) =>
          doesClassMatchFilters(
            cls,
            selectedClassesArray,
            selectedInstructorsArray
          )
        );
        if (hasMatch) return false;
      } else if (day.has_classes) {
        return false;
      }
    }
    return true;
  }, [
    currentWeekStart,
    visibleDays,
    calendarMonthClasses,
    calendarDayByKey,
    calendarMonth,
    selectedClassesArray,
    selectedInstructorsArray,
    hasFilters,
  ]);

  const firstAvailableDateInWindow = useMemo<Date | null>(() => {
    if (!currentWeekStart || !availableDayByKey || isLoading) {
      return null;
    }
    const todayLocal = startOfDay(new Date());
    for (let i = 0; i < visibleDays; i++) {
      const date = addDays(currentWeekStart, i);
      if (isBefore(date, todayLocal)) continue;
      const day = availableDayByKey.get(format(date, 'yyyy-MM-dd'));
      if (!day) continue;
      if (hasFilters) {
        const hasMatch = day.classes?.some((cls) =>
          doesClassMatchFilters(
            cls,
            selectedClassesArray,
            selectedInstructorsArray
          )
        );
        if (hasMatch) return date;
      } else if ((day.classes?.length ?? 0) > 0) {
        return date;
      }
    }
    return null;
  }, [
    currentWeekStart,
    visibleDays,
    availableDayByKey,
    isLoading,
    selectedClassesArray,
    selectedInstructorsArray,
    hasFilters,
  ]);

  const isCalendarDateDisabled = useCallback(
    (date: Date) => !calendarActiveDateKeys.has(format(date, 'yyyy-MM-dd')),
    [calendarActiveDateKeys]
  );

  const availableInstructors = useMemo(
    () => getInstructorsFromAvailability(availableClasses),
    [availableClasses]
  );

  const handleInstructorClick = useCallback(
    (adminUserId: string) => {
      const isSoleActive = selectedInstructor === adminUserId;
      setSelectedInstructor(isSoleActive ? 'all' : adminUserId);
      track({
        event: AnalyticsEvent.HOME_FILTER_CHANGED,
        properties: {
          filter_type: 'instructor',
          selected_count: isSoleActive ? 0 : 1,
          total_count: availableInstructors.length,
        },
      });
    },
    [selectedInstructor, setSelectedInstructor, availableInstructors.length]
  );

  const classOptions = useMemo(
    () => getClassListOptions(classList),
    [classList]
  );
  const instructorOptions = useMemo(
    () => getInstructorListOptions(availableInstructors),
    [availableInstructors]
  );

  const groupedFilterOptions: FilterSelectOption[] = useMemo(
    () => [
      ...classOptions.map((o) => ({
        label: o.label,
        value: `class:${o.value}`,
        group: 'Classes',
      })),
      ...instructorOptions.map((o) => ({
        label: o.label,
        value: `instructor:${o.value}`,
        group: 'Instructors',
      })),
    ],
    [classOptions, instructorOptions]
  );

  const groupedFilterValue: string[] = useMemo(
    () => [
      ...parseSelectedCsv(selectedClass).map((v) => `class:${v}`),
      ...parseSelectedCsv(selectedInstructor).map((v) => `instructor:${v}`),
    ],
    [selectedClass, selectedInstructor]
  );

  const handleGroupedFilterChange = useCallback((values: string[]) => {
    const classes = values
      .filter((v) => v.startsWith('class:'))
      .map((v) => v.slice(6));
    const instructors = values
      .filter((v) => v.startsWith('instructor:'))
      .map((v) => v.slice(11));
    setSelectedClassesArray(classes);
    setSelectedInstructorsArray(instructors);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setVisibleDays(getVisibleDays(window.innerWidth));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCalendar(false);
        calendarToggleRef.current?.focus();
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showCalendar]);

  useEffect(() => {
    if (selectedDate && !showCalendar) {
      setCalendarMonth(selectedDate);
    }
  }, [selectedDate, showCalendar]);

  useEffect(() => {
    if (didInitFromUrl.current) return;
    const urlDate = searchParams.get('date');
    const urlClass = searchParams.get('class_id');
    const urlInstructor = searchParams.get('instructor_id');

    if (urlDate || urlClass || urlInstructor) {
      if (urlDate) {
        const parsed = parseDateParam(urlDate);
        if (parsed) {
          const todayStart = startOfDay(new Date());
          const parsedStart = startOfDay(parsed);
          const target = isBefore(parsedStart, todayStart)
            ? todayStart
            : parsedStart;
          setSelectedDate(target);
          setCurrentWeekStart(
            isMobile ? getMobileWeekStart(target) : getWeekStart(target)
          );
        }
      }
      if (urlClass) {
        setSelectedClass(urlClass);
      }
      if (urlInstructor) {
        setSelectedInstructor(urlInstructor);
      }
    }
    didInitFromUrl.current = true;
  }, [
    searchParams,
    isMobile,
    setSelectedDate,
    setSelectedClass,
    setSelectedInstructor,
    setCurrentWeekStart,
  ]);

  // A preset category seeds the class filter once the groups arrive, unless
  // the URL already carries a filter the visitor chose. Set directly: the array
  // setter reports a filter change the visitor never made.
  const didSeedGroup = useRef(false);
  useEffect(() => {
    if (didSeedGroup.current || !groupId || !classGroups) return;
    didSeedGroup.current = true;
    if (searchParams.get('class_id')) return;
    const ids = classGroups
      .find((g) => g.class_group_id === groupId)
      ?.classes.map((c) => c.class_id);
    if (ids?.length) setSelectedClass(ids.join(','));
  }, [groupId, classGroups, searchParams, setSelectedClass]);

  useEffect(() => {
    if (!didInitFromUrl.current) return;
    const params = new URLSearchParams(searchParams.toString());
    if (selectedDate) {
      params.set('date', format(selectedDate, 'yyyy-MM-dd'));
    } else {
      params.delete('date');
    }
    if (selectedClass && selectedClass !== 'all') {
      params.set('class_id', selectedClass);
    } else {
      params.delete('class_id');
    }
    if (selectedInstructor && selectedInstructor !== 'all') {
      params.set('instructor_id', selectedInstructor);
    } else {
      params.delete('instructor_id');
    }

    const next = `${pathname}?${params.toString()}`;
    const current = `${pathname}?${searchParams.toString()}`;
    if (next !== current) {
      router.replace(next, { scroll: false });
    }
  }, [
    selectedDate,
    selectedClass,
    selectedInstructor,
    pathname,
    searchParams,
    router,
  ]);

  useEffect(() => {
    if (isRestoredFromStorage) {
      const today = startOfDay(new Date());
      const isPastDate = selectedDate && isBefore(selectedDate, today);
      // Only a week browsed without picking a day is judged by its start: the
      // current week's own start is already behind today.
      const isPastWeek =
        !selectedDate && currentWeekStart && currentWeekStart < today;

      if (isPastDate || isPastWeek) {
        setSelectedDate(today);
        setCurrentWeekStart(
          isMobile ? getMobileWeekStart(today) : getWeekStart(today)
        );
      }

      markAsNotRestored();
    }
  }, [
    isRestoredFromStorage,
    markAsNotRestored,
    selectedDate,
    currentWeekStart,
    selectedClass,
    selectedInstructor,
  ]);

  // Derived, not synced in an effect: the render that ends the loading state
  // would otherwise still hold the empty list and paint "No classes today"
  // for a frame before the cards.
  const filteredClasses = useMemo(() => {
    if (!selectedDate || !availableClasses) return [];
    const selectedKey = format(selectedDate, 'yyyy-MM-dd');
    return availableClasses.days
      .filter((availableDate) => availableDate.date === selectedKey)
      .flatMap((availableDate) => availableDate.classes || []);
  }, [availableClasses, selectedDate]);

  useEffect(() => {
    if (currentWeekStart && !isRestoredFromStorage && !isLoading) {
      const today = startOfDay(new Date());
      const weekEnd = addDays(currentWeekStart, visibleDays - 1);

      if (!isClosestClassNavigation) {
        const selectedKey = selectedDate
          ? format(selectedDate, 'yyyy-MM-dd')
          : null;
        const isSelectedDateInCurrentWeek =
          selectedKey !== null &&
          selectedKey >= format(currentWeekStart, 'yyyy-MM-dd') &&
          selectedKey <= format(weekEnd, 'yyyy-MM-dd');

        if (!selectedDate || !isSelectedDateInCurrentWeek) {
          if (firstAvailableDateInWindow) {
            setSelectedDate(firstAvailableDateInWindow);
          } else if (today >= currentWeekStart && today <= weekEnd) {
            setSelectedDate(today);
          } else if (currentWeekStart >= new Date()) {
            setSelectedDate(currentWeekStart);
          }
        }
      }
      setIsClosestClassNavigation(false);
    }
  }, [
    currentWeekStart,
    selectedDate,
    isRestoredFromStorage,
    isLoading,
    firstAvailableDateInWindow,
  ]);

  const handleWeekChange = (windows: number) => {
    if (!currentWeekStart) return;

    setSelectedDate(null);
    setCurrentWeekStart(addDays(currentWeekStart, visibleDays * windows));
    track({
      event: AnalyticsEvent.HOME_WEEK_NAVIGATED,
      properties: {
        direction: windows < 0 ? 'prev' : 'next',
        visible_days: visibleDays,
      },
    });
  };

  const handleGoToToday = (): void => {
    setIsClosestClassNavigation(false);
    setSelectedDate(today);
    setCurrentWeekStart(
      isMobile ? getMobileWeekStart(today) : getWeekStart(today)
    );
    resetFilter();
  };

  const classesForTheDay = filteredClasses.filter((cls) =>
    doesClassMatchFilters(cls, selectedClassesArray, selectedInstructorsArray)
  );

  const resetFilter = () => {
    setSelectedClass('all');
    setSelectedInstructor('all');
  };

  const handleCalendarDateSelect = (selected: Date | undefined) => {
    if (!selected) return;
    setSelectedDate(selected);
    setCurrentWeekStart(
      isMobile ? getMobileWeekStart(selected) : getWeekStart(selected)
    );
    setShowCalendar(false);
    track({
      event: AnalyticsEvent.HOME_DATE_PICKER_SELECTED,
      properties: { date: format(selected, 'yyyy-MM-dd') },
    });
  };

  if (!isInitialized || isClassGroupsLoading) {
    return <DailyViewSkeleton classSettings={classSettings} />;
  }

  if (!hasClasses) {
    return <NoClassesYet />;
  }

  const renderDates = () => {
    return [...Array(visibleDays)].map((_, i) => {
      // Formatted from the day itself, never from its key: date-fns reads a
      // 'yyyy-MM-dd' string as UTC midnight, the evening before west of UTC,
      // which put every US visitor's strip a day early.
      const dateObj = startOfDay(addDays(currentWeekStart || new Date(), i));
      const date = format(dateObj, 'yyyy-MM-dd');
      const isSelected = selectedDate
        ? date === format(selectedDate, 'yyyy-MM-dd')
        : false;
      const dateString = format(dateObj, 'EEE MMM dd yyyy');
      const isLastDayOfMonth =
        format(dateObj, 'dd') === format(lastDayOfMonth(dateObj), 'dd');
      const isLastIndexofWeek = visibleDays - 1;

      const dayForDate = availableDayByKey?.get(date);
      const hasAvailableClasses = dayForDate?.classes.some((cls) =>
        doesClassMatchFilters(
          cls,
          selectedClassesArray,
          selectedInstructorsArray
        )
      );

      const isDisabledDate = isBefore(dateObj, today) || !hasAvailableClasses;

      // Bookable days use the body color, never gray-900: the tenant heading
      // color must not bleed into controls. Selection and hover carry the
      // button color. gray-450 keeps unavailable days at 3.1:1 on the card,
      // where gray-400 sits at an unreadable 2.4:1.
      const dayTextClass =
        isSelected || (hoveredDate === dateString && !isDisabledDate)
          ? 'text-red-500'
          : isDisabledDate
            ? 'text-gray-450'
            : 'text-gray-700';

      const dayTab = (
        <button
          type="button"
          // Only mobile keeps it activatable: tapping an empty day toggles its
          // "No classes" bubble. On desktop activation is a no-op, so a real
          // disabled attribute keeps it out of the tab order.
          disabled={isDisabledDate && !isMobile}
          aria-disabled={isDisabledDate && isMobile ? true : undefined}
          aria-current={isSelected ? 'date' : undefined}
          aria-label={format(dateObj, 'EEEE, MMM d')}
          onClick={() => {
            if (!isDisabledDate) {
              setSelectedDate(dateObj);
              track({
                event: AnalyticsEvent.HOME_DATE_SELECTED,
                properties: {
                  date,
                  day_of_week: format(dateObj, 'EEEE'),
                  is_today: date === format(today, 'yyyy-MM-dd'),
                },
              });
            } else if (isMobile) {
              setHoveredDate(hoveredDate === dateString ? null : dateString);
            }
          }}
          {...(!isMobile
            ? {
                onMouseEnter: () => setHoveredDate(dateString),
                onMouseLeave: () => setHoveredDate(null),
              }
            : {})}
          className={mergeClassNames(
            'relative flex-shrink-0 rounded-lg py-1.5 transition-all',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2',
            isMobile ? 'w-full' : 'w-16',
            isDisabledDate
              ? 'cursor-not-allowed text-gray-450'
              : isSelected
                ? 'cursor-pointer bg-red-50 text-red-500'
                : 'cursor-pointer hover:bg-red-50 hover:text-red-500'
          )}
        >
          <span
            className={mergeClassNames(
              'block w-full text-center text-sm font-bold',
              dayTextClass
            )}
          >
            {format(dateObj, 'EEE')}
          </span>
          <span
            className={mergeClassNames(
              'block w-full text-center text-sm',
              dayTextClass
            )}
          >
            {format(dateObj, 'd')}
          </span>
        </button>
      );

      return (
        <Fragment key={i}>
          {isMobile ? (
            // Shrinks from w-12, as the skeleton's days do, where four days,
            // the arrows and a month divider are wider than the card: a 320
            // screen.
            <div className="relative w-12 min-w-0">
              {dayTab}
              {hoveredDate === dateString && isDisabledDate && (
                <div className="absolute -top-11 left-1/2 z-50 -translate-x-1/2">
                  <div className="whitespace-nowrap rounded-xl bg-ink px-2.5 py-2 text-center text-sm font-medium text-white shadow-lg">
                    No classes
                    <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 border-x-[6px] border-t-[6px] border-x-transparent border-t-ink" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Tooltip
              content="No classes"
              isDisabled={!isDisabledDate}
              side="top"
            >
              {dayTab}
            </Tooltip>
          )}
          {isLastDayOfMonth && i !== isLastIndexofWeek && (
            <div className="h-[52px] w-0 border-r-2 border-dashed border-gray-300"></div>
          )}
        </Fragment>
      );
    });
  };

  const isBackDisabled = currentWeekStart
    ? today >= currentWeekStart || isPrevWindowEmpty
    : false;
  const isNextDisabled = currentWeekStart ? isNextWindowEmpty : false;

  const toggleNavTooltip = (button: 'prev' | 'next') =>
    setHoveredNavButton((current) => (current === button ? null : button));

  return (
    <>
      <div className="mx-auto w-full rounded-lg bg-white">
        <div className="flex flex-col gap-5">
          <div className="flex flex-row items-center justify-between gap-4">
            <div
              ref={calendarRef}
              className="relative flex shrink-0 items-center gap-3"
            >
              <MokoLink
                ref={calendarToggleRef}
                size="md"
                color="red"
                className="whitespace-nowrap"
                aria-expanded={showCalendar}
                iconLeft={
                  showCalendar ? <X size={18} /> : <Calendar size={18} />
                }
                isStaticIcon
                onClick={() => {
                  const next = !showCalendar;
                  setShowCalendar(next);
                  if (next) {
                    track({ event: AnalyticsEvent.HOME_DATE_PICKER_OPENED });
                  }
                }}
              >
                {/* Reads the visible window, not the selection: handleWeekChange
                    clears selectedDate and the re-pick waits on the fetch, which
                    would blank the label down to a bare icon mid-navigation. */}
                {(selectedDate ?? currentWeekStart)?.toLocaleDateString(
                  'en-US',
                  { month: 'long', year: 'numeric' }
                )}
              </MokoLink>

              {showCalendar && (
                <div className="absolute left-0 top-[37px] z-30">
                  <MokoDatePicker
                    variant="scheduler"
                    selected={selectedDate ?? undefined}
                    onSelect={handleCalendarDateSelect}
                    activeDates={calendarActiveDates}
                    startMonth={startOfDay(new Date())}
                    endMonth={addDays(new Date(), 30)}
                    disabled={isCalendarDateDisabled}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                  />
                </div>
              )}
            </div>

            {isMobile ? (
              // The reveal stacks its two layers in a grid, which would hold
              // the trigger at its full label width; min-w-0 on the layers
              // lets a long pick truncate into the space the month leaves.
              <SkeletonReveal
                isLoading={isLoading}
                className="min-w-0 [&>*]:min-w-0"
                skeleton={<FilterTriggerSkeleton width={FILTER_WIDTH_MOBILE} />}
              >
                <FilterSelect
                  innerLabel="Filter"
                  options={groupedFilterOptions}
                  value={groupedFilterValue}
                  onChange={handleGroupedFilterChange}
                  isDisabled={isLoading || groupedFilterOptions.length === 0}
                  maxDisplayItems={1}
                  panelMinWidth={FILTER_PANEL_MIN_WIDTH}
                />
              </SkeletonReveal>
            ) : (
              <SkeletonReveal
                isLoading={isLoading}
                skeleton={
                  <div className="flex flex-row flex-wrap gap-3">
                    <FilterTriggerSkeleton width={FILTER_WIDTH_CLASSES} />
                    <FilterTriggerSkeleton width={FILTER_WIDTH_INSTRUCTORS} />
                  </div>
                }
              >
                <div className="flex flex-row flex-wrap gap-3">
                  <div className="min-w-0 max-w-[15rem]">
                    <FilterSelect
                      innerLabel="Classes"
                      options={classOptions}
                      value={parseSelectedCsv(selectedClass)}
                      onChange={setSelectedClassesArray}
                      isDisabled={isLoading || classOptions.length === 0}
                      panelMinWidth={FILTER_PANEL_MIN_WIDTH}
                    />
                  </div>
                  <div className="min-w-0 max-w-[15rem]">
                    <FilterSelect
                      innerLabel="Instructors"
                      options={instructorOptions}
                      value={parseSelectedCsv(selectedInstructor)}
                      onChange={setSelectedInstructorsArray}
                      isDisabled={isLoading || instructorOptions.length === 0}
                      panelMinWidth={FILTER_PANEL_MIN_WIDTH}
                    />
                  </div>
                </div>
              </SkeletonReveal>
            )}
          </div>
          <Separator className="hidden sm:block" />

          <div className="flex items-center justify-between">
            <NavButton
              direction="prev"
              isMobile={isMobile}
              isDisabled={isBackDisabled}
              isTooltipOpen={hoveredNavButton === 'prev'}
              tooltipText="No earlier classes"
              onClick={() => handleWeekChange(-1)}
              onMobileToggle={() => toggleNavTooltip('prev')}
            />
            <div
              className={mergeClassNames(
                'flex min-w-0 flex-1 items-center justify-center',
                isMobile ? 'mx-2 gap-2' : 'gap-3'
              )}
            >
              {renderDates()}
            </div>
            <NavButton
              direction="next"
              isMobile={isMobile}
              isDisabled={isNextDisabled}
              isTooltipOpen={hoveredNavButton === 'next'}
              tooltipText="No upcoming classes"
              onClick={() => handleWeekChange(1)}
              onMobileToggle={() => toggleNavTooltip('next')}
            />
          </div>
        </div>
      </div>

      {/* A week change clears the day until the effect above picks one. The
          skeleton holds the list's height for that render: an empty list
          shrank the page, and a phone scrolled back to the top. A failed
          read says so: the empty state below would claim no classes. */}
      {availabilityError && !availableClasses ? (
        <ClassesLoadError />
      ) : isLoading || !selectedDate ? (
        <ClassListSkeleton classSettings={classSettings} />
      ) : classesForTheDay && classesForTheDay?.length > 0 ? (
        classesForTheDay.map((item, index) => {
          const classData = mapClassData(item, selectedDate);

          return (
            <ClassCard
              key={index}
              classData={classData}
              classSettings={classSettings}
              onInstructorClick={handleInstructorClick}
            />
          );
        })
      ) : (
        (selectedClass || selectedInstructor) &&
        selectedDate && (
          <ClosestAvailableClass
            selectedClass={selectedClass}
            selectedInstructor={selectedInstructor}
            selectedDate={selectedDate}
            allClasses={availableClasses?.days ?? []}
            nextWeekClasses={availableClasses?.next_week_days ?? []}
            setSelectedDate={setSelectedDate}
            classList={classList}
            instructorList={availableInstructors}
            handleWeekChange={handleWeekChange}
            isMobile={isMobile}
            visibleDays={visibleDays}
            setIsClosestClassNavigation={setIsClosestClassNavigation}
            currentWeekStart={currentWeekStart || new Date()}
          />
        )
      )}
    </>
  );
};
